import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { User } from '@services/AssessmentService';
import './AddRelationshipModal.css';

interface AddRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RelationshipData) => Promise<void>;
  users: User[];
  loading: boolean;
}

export interface RelationshipData {
  user_id: number;
  managers: ManagerData[];
}

export interface ManagerData {
  // If manager exists in system
  manager_id?: number;
  // If manager doesn't exist in system
  manager_name?: string;
  manager_email?: string;
}

const AddRelationshipModal: React.FC<AddRelationshipModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  users,
  loading
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedManagers, setSelectedManagers] = useState<User[]>([]);
  const [manualManagers, setManualManagers] = useState<{ name: string; email: string }[]>([]);
  const [managerType, setManagerType] = useState<'existing' | 'manual'>('existing');
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualEmailError, setManualEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setSelectedUser(null);
      setSelectedUserId('');
      setSelectedManagers([]);
      setManualManagers([]);
      setManagerType('existing');
      setManualName('');
      setManualEmail('');
      setManualEmailError('');
    }
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleManualEmailChange = (email: string) => {
    setManualEmail(email);
    if (email && !validateEmail(email)) {
      setManualEmailError('Please enter a valid email address');
    } else {
      setManualEmailError('');
    }
  };

  const handleAddManualManager = () => {
    if (!manualName.trim() || !manualEmail.trim()) {
      alert('Please enter both name and email');
      return;
    }

    if (!validateEmail(manualEmail)) {
      setManualEmailError('Please enter a valid email address');
      return;
    }

    // Check if email already added
    const emailExists = manualManagers.some(m => m.email.toLowerCase() === manualEmail.toLowerCase());
    if (emailExists) {
      alert('This email has already been added');
      return;
    }

    setManualManagers([...manualManagers, { name: manualName.trim(), email: manualEmail.trim() }]);
    setManualName('');
    setManualEmail('');
    setManualEmailError('');
  };

  const handleRemoveManualManager = (index: number) => {
    setManualManagers(manualManagers.filter((_, i) => i !== index));
  };

  const getAvailableManagers = () => {
    if (!selectedUserId) return [];
    return users.filter(u => u.userId !== selectedUserId);
  };

  const handleSubmit = async () => {
    if (!selectedUserId) {
      alert('Please select an employee');
      return;
    }

    const managers: ManagerData[] = [];

    // Add existing managers
    selectedManagers.forEach(manager => {
      managers.push({ manager_id: manager.userId });
    });

    // Add manual managers
    manualManagers.forEach(manager => {
      managers.push({
        manager_name: manager.name,
        manager_email: manager.email
      });
    });

    if (managers.length === 0) {
      alert('Please select at least one manager or add a manual manager');
      return;
    }

    const relationshipData: RelationshipData = {
      user_id: selectedUserId as number,
      managers
    };

    setSubmitting(true);
    try {
      await onSubmit(relationshipData);
      onClose();
    } catch (error) {
      console.error('Error submitting relationship:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="add-relationship-modal-overlay" onClick={onClose}>
      <div className="add-relationship-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="add-relationship-modal-header">
          <h3>Add Reporting Manager Relationship</h3>
          <button className="add-relationship-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="add-relationship-modal-body">
          {/* Select Employee */}
          <div className="add-relationship-form-group">
            <label htmlFor="selectUser">Select Employee *</label>
            <Autocomplete
              id="selectUser"
              options={users}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
                setSelectedUserId(newValue ? newValue.userId : '');
                setSelectedManagers([]);
              }}
              disabled={loading || submitting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and select employee..."
                  variant="outlined"
                />
              )}
              isOptionEqualToValue={(option, value) => option.userId === value.userId}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '4px',
                }
              }}
            />
          </div>

          {selectedUserId && (
            <>
              {/* Manager Type Selection */}
              <div className="add-relationship-form-group">
                <label>Manager Type</label>
                <div className="add-relationship-manager-type-selector">
                  <button
                    type="button"
                    className={`add-relationship-type-btn ${managerType === 'existing' ? 'active' : ''}`}
                    onClick={() => setManagerType('existing')}
                  >
                    Select Existing Users
                  </button>
                  <button
                    type="button"
                    className={`add-relationship-type-btn ${managerType === 'manual' ? 'active' : ''}`}
                    onClick={() => setManagerType('manual')}
                  >
                    Add Manual Entry
                  </button>
                </div>
              </div>

              {/* Existing Managers Selection */}
              {managerType === 'existing' && (
                <div className="add-relationship-form-group">
                  <label>Select Managers from Users</label>
                  <Autocomplete
                    multiple
                    options={getAvailableManagers()}
                    getOptionLabel={(option) => `${option.name} (${option.email})`}
                    value={selectedManagers}
                    onChange={(event, newValue) => {
                      setSelectedManagers(newValue);
                    }}
                    disabled={loading || submitting}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select managers..."
                        variant="outlined"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          sx={{
                            backgroundColor: '#667eea',
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'rgba(255, 255, 255, 0.7)',
                              '&:hover': {
                                color: 'white',
                              }
                            }
                          }}
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option.userId === value.userId}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: '4px',
                      }
                    }}
                  />
                  {selectedManagers.length > 0 && (
                    <div className="add-relationship-count-badge">
                      {selectedManagers.length} manager(s) selected
                    </div>
                  )}
                </div>
              )}

              {/* Manual Manager Entry */}
              {managerType === 'manual' && (
                <div className="add-relationship-manual-section">
                  <div className="add-relationship-manual-inputs">
                    <div className="add-relationship-form-group">
                      <label htmlFor="managerName">Manager Name *</label>
                      <input
                        id="managerName"
                        type="text"
                        className="add-relationship-form-control"
                        placeholder="Enter manager name"
                        value={manualName}
                        onChange={(e) => setManualName(e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                    <div className="add-relationship-form-group">
                      <label htmlFor="managerEmail">Manager Email *</label>
                      <input
                        id="managerEmail"
                        type="email"
                        className={`add-relationship-form-control ${manualEmailError ? 'error' : ''}`}
                        placeholder="manager@example.com"
                        value={manualEmail}
                        onChange={(e) => handleManualEmailChange(e.target.value)}
                        disabled={submitting}
                      />
                      {manualEmailError && (
                        <span className="add-relationship-error-text">{manualEmailError}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="add-relationship-btn-add-manual"
                    onClick={handleAddManualManager}
                    disabled={!manualName.trim() || !manualEmail.trim() || !!manualEmailError || submitting}
                  >
                    + Add Manager
                  </button>

                  {/* List of manual managers */}
                  {manualManagers.length > 0 && (
                    <div className="add-relationship-manual-list">
                      <h4>Added Managers ({manualManagers.length})</h4>
                      {manualManagers.map((manager, index) => (
                        <div key={index} className="add-relationship-manual-item">
                          <div className="add-relationship-manual-info">
                            <span className="add-relationship-manual-name">{manager.name}</span>
                            <span className="add-relationship-manual-email">{manager.email}</span>
                          </div>
                          <button
                            type="button"
                            className="add-relationship-btn-remove"
                            onClick={() => handleRemoveManualManager(index)}
                            disabled={submitting}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Total Count */}
              {(selectedManagers.length > 0 || manualManagers.length > 0) && (
                <div className="add-relationship-total-count">
                  Total managers to be added: <strong>{selectedManagers.length + manualManagers.length}</strong>
                </div>
              )}
            </>
          )}
        </div>

        <div className="add-relationship-modal-footer">
          <button
            className="add-relationship-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="add-relationship-btn-primary"
            onClick={handleSubmit}
            disabled={
              loading ||
              submitting ||
              !selectedUserId ||
              (selectedManagers.length === 0 && manualManagers.length === 0)
            }
          >
            {submitting ? 'Adding...' : 'Add Relationship(s)'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRelationshipModal;
